import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { Textarea } from "../components/ui/textarea";
import {
  Baby, CalendarDays, ArrowUpRight, RefreshCw, Plus, Trash2,
  Gift, FileText, HeartHandshake, Sparkles, Apple, Moon, Activity,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  BlobSticker, StarSticker, HeartSticker, FlowerSticker, SparkBurst,
} from "../components/DecorativeShapes";
import {
  getPregnancyProfile, upsertPregnancyProfile, deletePregnancyProfile,
  getPregnancyState, getPregnancyInsight,
  logPregnancySymptom, listPregnancySymptoms,
} from "../lib/api";

/* Lunara-inspired pregnancy companion \u2014 soft pastel pink, coral primary,
   week-strip top, baby illustration hero, multi-pastel insight cards. */

const PREG_SYMPTOMS = [
  "Nausea", "Fatigue", "Heartburn", "Back pain",
  "Headache", "Cramps", "Swelling", "Insomnia",
  "Mood swings", "Cravings", "Constipation", "Braxton-Hicks",
];

const TRIMESTER_NAMES = { 1: "First", 2: "Second", 3: "Third" };

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

function fmtSafe(iso, pattern = "EEE, MMM d") {
  try { return iso ? format(new Date(iso), pattern) : "\u2014"; } catch { return "\u2014"; }
}

function buildWeekStrip(centerDate = new Date()) {
  // Build 7 days centered on today (Mon-Sun style); start from Sunday of this week.
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(today);
  start.setDate(today.getDate() - today.getDay()); // Sunday
  const out = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    out.push({
      date: d,
      day: d.getDate(),
      weekday: WEEKDAYS[d.getDay()],
      isToday: d.toDateString() === today.toDateString(),
      isFuture: d > today,
    });
  }
  return out;
}

export default function PregnancyPage({ profile }) {
  const [profileDoc, setProfileDoc] = useState(null);
  const [state, setState] = useState(null);
  const [insight, setInsight] = useState(null);
  const [symptoms, setSymptoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [insightRefreshing, setInsightRefreshing] = useState(false);

  const [setupOpen, setSetupOpen] = useState(false);
  const [lmpDate, setLmpDate] = useState(null);

  const [symptomOpen, setSymptomOpen] = useState(false);
  const [selectedSymptoms, setSelectedSymptoms] = useState([]);
  const [symptomNote, setSymptomNote] = useState("");

  const weekStrip = buildWeekStrip();

  const refresh = async () => {
    try {
      const [p, st, sym] = await Promise.all([
        getPregnancyProfile(),
        getPregnancyState(),
        listPregnancySymptoms(),
      ]);
      setProfileDoc(p);
      setState(st);
      setSymptoms(sym || []);
      if (st?.has_data) {
        try { setInsight(await getPregnancyInsight(false)); } catch {}
      }
    } catch {}
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const startSetup = () => {
    setLmpDate(profileDoc?.lmp_date ? new Date(profileDoc.lmp_date) : null);
    setSetupOpen(true);
  };

  const submitLmp = async () => {
    if (!lmpDate) { toast.error("Pick your last period start date."); return; }
    try {
      await upsertPregnancyProfile({ lmp_date: format(lmpDate, "yyyy-MM-dd"), status: "active" });
      toast.success("Your pregnancy timeline is ready.");
      setSetupOpen(false);
      await refresh();
    } catch { toast.error("Could not save."); }
  };

  const refreshInsight = async () => {
    setInsightRefreshing(true);
    try { setInsight(await getPregnancyInsight(true)); }
    catch { toast.error("Could not refresh insight."); }
    setInsightRefreshing(false);
  };

  const submitSymptoms = async () => {
    if (selectedSymptoms.length === 0) { toast.error("Select at least one symptom."); return; }
    try {
      await logPregnancySymptom({ symptoms: selectedSymptoms, note: symptomNote });
      toast.success("Logged.");
      setSymptomOpen(false);
      setSelectedSymptoms([]);
      setSymptomNote("");
      await refresh();
    } catch { toast.error("Could not log."); }
  };

  const removePregnancy = async () => {
    if (!window.confirm("Remove your pregnancy data? You can add it back any time.")) return;
    try {
      await deletePregnancyProfile();
      toast.success("Pregnancy data cleared.");
      setProfileDoc(null);
      setState(null);
      setInsight(null);
      setSymptoms([]);
    } catch { toast.error("Could not remove."); }
  };

  const firstName = (profile?.name || "").split(" ")[0];
  const hasData = state?.has_data && (state?.current_week || 0) > 0;
  const trimester = state?.trimester;

  // Build the pregnancy due-date weekday display (e.g. "41 weeks, 1 day" style)
  const weekLabel = hasData
    ? `${state.current_week} week${state.current_week !== 1 ? "s" : ""}, ${state.current_day_of_week} day${state.current_day_of_week !== 1 ? "s" : ""}`
    : "";

  return (
    <div className="pregnancy-theme kanya-page-pad-bottom" data-testid="pregnancy-page">
      {/* === HEADER (greeting + avatar) === */}
      <header className="px-5 pt-7 pb-3 flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[hsl(348_35%_40%)] font-semibold">
            {firstName ? `Hi, ${firstName}` : "Welcome"}
          </p>
          <h1 className="font-editorial text-[28px] leading-[1.05] mt-1 text-[hsl(348_55%_22%)]">
            Your little one
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {hasData && (
            <button onClick={removePregnancy} aria-label="Reset pregnancy" data-testid="pregnancy-reset"
              className="w-10 h-10 rounded-full bg-white/80 border border-[hsl(348_40%_88%)] flex items-center justify-center text-[hsl(348_50%_45%)] hover:bg-white">
              <Trash2 size={15} />
            </button>
          )}
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[hsl(348_75%_70%)] to-[hsl(348_70%_60%)] flex items-center justify-center text-white font-editorial text-[18px]">
            {firstName ? firstName[0]?.toUpperCase() : "K"}
          </div>
        </div>
      </header>

      {/* === WEEK STRIP (Lunara-style horizontal date row) === */}
      <div className="px-5">
        <div className="surface-pink-card rounded-[26px] px-3 py-3 flex items-center justify-between" data-testid="pregnancy-week-strip">
          {weekStrip.map((d, i) => (
            <div
              key={i}
              className={`week-day-cell ${d.isToday ? "is-today" : ""} ${d.isFuture ? "is-future" : ""}`}
            >
              <span className="text-[10px] uppercase tracking-wider font-semibold text-[hsl(348_30%_50%)]">{d.weekday}</span>
              <span className="day-num">{d.day}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="px-5 space-y-4 mt-4">
        {loading && (
          <div className="surface-pink-card rounded-[28px] p-6">
            <div className="shimmer h-5 w-1/2 rounded-full" />
            <div className="shimmer h-4 w-3/4 rounded-full mt-3" />
          </div>
        )}

        {!loading && !hasData && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="surface-pink-card rounded-[28px] p-6 relative overflow-hidden"
            data-testid="pregnancy-empty"
          >
            <BlobSticker size={140} color="#FFC4D6" className="absolute -top-12 -right-10 float-slow opacity-90" />
            <HeartSticker size={42} color="#FFA9C0" className="absolute top-6 right-24 float-mid" />
            <div className="relative">
              <p className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[hsl(348_50%_45%)]">Begin</p>
              <h2 className="font-editorial text-[30px] leading-[1.05] mt-2 max-w-[300px] text-[hsl(348_55%_22%)]">
                A new chapter, gently tracked.
              </h2>
              <p className="text-[14px] mt-3 leading-relaxed font-body text-[hsl(348_40%_35%)] max-w-[300px]">
                Share the start of your last period and Knya will quietly map your weeks, your baby's growth, and what your body needs.
              </p>
              <button onClick={startSetup} className="mt-5 btn-pill-coral" data-testid="pregnancy-start-setup">
                <Baby size={16} /> Start your timeline
              </button>
            </div>
          </motion.div>
        )}

        {!loading && hasData && (
          <>
            {/* ===== BABY HERO (light pink card, big plush sphere as baby) ===== */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="surface-pink-card rounded-[32px] p-6 relative overflow-hidden"
              data-testid="pregnancy-week-hero"
            >
              {/* Soft womb-shape background blob */}
              <div
                className="absolute inset-0 -z-0"
                style={{
                  background: "radial-gradient(closest-side at 60% 35%, hsl(348 80% 96%) 0%, transparent 70%)",
                }}
              />

              <div className="relative flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[hsl(348_50%_45%)]">
                    {TRIMESTER_NAMES[trimester]} trimester
                  </p>
                  <p className="font-editorial text-[34px] leading-[1.0] mt-2 text-[hsl(348_55%_22%)]" data-testid="pregnancy-current-week">
                    {weekLabel}
                  </p>
                </div>
                <SparkBurst size={36} color="#FFB8C7" className="float-mid" />
              </div>

              {/* Centered "baby" plush sphere */}
              <div className="relative flex justify-center my-5">
                <div
                  className="w-[170px] h-[170px] rounded-full relative"
                  style={{
                    background: `radial-gradient(circle at 32% 30%, #FFFFFF 0%, #FFC4D6 45%, #E58FA8 100%)`,
                    boxShadow: "inset -10px -16px 36px rgba(170, 60, 90, 0.30), inset 12px 14px 26px rgba(255, 255, 255, 0.70), 0 30px 60px -20px rgba(220, 90, 130, 0.45)",
                  }}
                  aria-hidden
                >
                  {/* Tiny floating heart accent */}
                  <div className="absolute -top-1 -right-2 float-mid">
                    <HeartSticker size={28} color="#FF89A1" />
                  </div>
                  <div className="absolute -bottom-1 -left-2 float-slow">
                    <StarSticker size={22} color="#FFD580" />
                  </div>
                </div>
              </div>

              <div className="relative text-center">
                <p className="font-editorial text-[22px] leading-tight text-[hsl(348_55%_22%)]">
                  About the size of {state.week_info?.size?.toLowerCase() || "a sweet bean"}.
                </p>
                <p className="text-[13px] mt-1.5 font-body text-[hsl(348_40%_35%)] max-w-[300px] mx-auto">
                  {state.week_info?.baby}
                </p>
              </div>

              {/* Progress bar + chips */}
              <div className="relative mt-5">
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "hsla(348, 30%, 80%, 0.4)" }}>
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${state.progress_percent || 0}%` }}
                    transition={{ duration: 1, ease: "easeOut" }}
                    className="h-full"
                    style={{ background: "linear-gradient(90deg, hsl(348 75% 72%), hsl(36 80% 70%))" }}
                  />
                </div>
                <div className="flex items-center justify-between text-[11px] text-[hsl(348_35%_40%)] mt-2 font-medium">
                  <span>{state.progress_percent}% of journey</span>
                  <span>
                    {state.days_until_due > 0
                      ? `${state.days_until_due} days to due date`
                      : state.days_until_due === 0
                        ? "Due today"
                        : `${-state.days_until_due} days past due`}
                  </span>
                </div>
              </div>

              <div className="relative mt-4 flex gap-2 justify-center">
                <span className="chip chip-coral"><CalendarDays size={11} /> Due {fmtSafe(state.edd_date, "MMM d, yyyy")}</span>
              </div>

              <button
                onClick={() => setSymptomOpen(true)}
                className="relative mt-5 btn-pill-coral w-full"
                data-testid="pregnancy-log-symptom-button"
              >
                Log today · how is your body? <ArrowUpRight size={14} />
              </button>
            </motion.div>

            {/* ===== MY DAILY INSIGHTS (multi-pastel cards Lunara-style) ===== */}
            <div data-testid="pregnancy-daily-insights">
              <p className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[hsl(348_50%_45%)] mb-2 px-1">
                My daily insights
              </p>
              <div className="grid grid-cols-2 gap-3">
                {/* Insight from AI */}
                <div className="col-span-2 surface-mint-soft plush-shadow-sm rounded-[24px] p-4 relative overflow-hidden" data-testid="pregnancy-insight">
                  <FlowerSticker size={40} color="rgba(255,255,255,0.6)" className="absolute -top-3 -right-3 float-slow" />
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex w-8 h-8 rounded-full bg-white items-center justify-center">
                        <HeartHandshake size={14} className="text-[hsl(150_40%_30%)]" />
                      </span>
                      <p className="text-[10px] uppercase tracking-[0.22em] font-semibold opacity-70">Knya for you</p>
                    </div>
                    <button onClick={refreshInsight} aria-label="Refresh" className="opacity-50 hover:opacity-100" data-testid="pregnancy-insight-refresh">
                      <RefreshCw size={13} className={insightRefreshing ? "animate-spin" : ""} />
                    </button>
                  </div>
                  <p className="font-editorial text-[19px] leading-tight mt-2.5" data-testid="pregnancy-insight-title">
                    {insight?.title || "A gentle note"}
                  </p>
                  <p className="text-[12.5px] opacity-80 mt-1.5 leading-snug font-body">
                    {insight?.body || "Pull to refresh for your weekly insight."}
                  </p>
                </div>

                {/* This week — mom */}
                <div className="surface-lavender-soft plush-shadow-sm rounded-[24px] p-4 relative overflow-hidden">
                  <SparkBurst size={20} color="rgba(255,255,255,0.7)" className="absolute top-3 right-3" />
                  <span className="inline-flex w-8 h-8 rounded-full bg-white items-center justify-center">
                    <Sparkles size={14} className="text-[hsl(268_40%_35%)]" />
                  </span>
                  <p className="text-[10px] uppercase tracking-[0.22em] font-semibold opacity-70 mt-2.5">For you</p>
                  <p className="text-[12.5px] mt-1 leading-snug font-body opacity-90">
                    {state.week_info?.mom}
                  </p>
                </div>

                {/* For baby */}
                <div className="surface-sky-soft plush-shadow-sm rounded-[24px] p-4 relative overflow-hidden">
                  <StarSticker size={20} color="rgba(255,255,255,0.8)" className="absolute top-3 right-3" />
                  <span className="inline-flex w-8 h-8 rounded-full bg-white items-center justify-center">
                    <Baby size={14} className="text-[hsl(210_40%_30%)]" />
                  </span>
                  <p className="text-[10px] uppercase tracking-[0.22em] font-semibold opacity-70 mt-2.5">For baby</p>
                  <p className="font-editorial text-[18px] leading-tight mt-1 capitalize">{state.week_info?.size}</p>
                  <p className="text-[12px] opacity-85 mt-1 leading-snug font-body">
                    {state.week_info?.baby}
                  </p>
                </div>

                {/* Nourish suggestion */}
                <div className="surface-butter-soft plush-shadow-sm rounded-[24px] p-4 relative overflow-hidden">
                  <BlobSticker size={42} color="rgba(255,255,255,0.55)" className="absolute -top-3 -right-3 float-mid" />
                  <span className="inline-flex w-8 h-8 rounded-full bg-white items-center justify-center">
                    <Apple size={14} className="text-[hsl(36_50%_30%)]" />
                  </span>
                  <p className="text-[10px] uppercase tracking-[0.22em] font-semibold opacity-70 mt-2.5">Nourish</p>
                  <p className="text-[12.5px] mt-1 leading-snug font-body opacity-90">
                    Leafy greens, hydration & gentle protein support both of you this week.
                  </p>
                </div>

                {/* Rest suggestion */}
                <div className="surface-pink-soft plush-shadow-sm rounded-[24px] p-4 relative overflow-hidden">
                  <HeartSticker size={28} color="#FFA9C0" className="absolute -top-2 -right-2 float-slow" />
                  <span className="inline-flex w-8 h-8 rounded-full bg-white items-center justify-center">
                    <Moon size={14} className="text-[hsl(348_45%_35%)]" />
                  </span>
                  <p className="text-[10px] uppercase tracking-[0.22em] font-semibold opacity-70 mt-2.5">Rest</p>
                  <p className="text-[12.5px] mt-1 leading-snug font-body opacity-90">
                    Side-lying sleep with a pillow between knees softens lower back tension.
                  </p>
                </div>
              </div>
            </div>

            {/* ===== Symptoms log section ===== */}
            <div className="surface-pink-card rounded-[28px] p-5 relative overflow-hidden" data-testid="pregnancy-symptoms">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[hsl(348_50%_45%)]">Symptoms</p>
                  <p className="font-editorial text-[22px] leading-tight mt-1 text-[hsl(348_55%_22%)]">How is your body today?</p>
                </div>
                <button onClick={() => setSymptomOpen(true)} className="btn-pill-coral !h-10 !px-4 text-[13px]" data-testid="pregnancy-quick-symptom">
                  <Plus size={14} /> Log
                </button>
              </div>
              {symptoms.length === 0 ? (
                <p className="text-[13px] mt-3 font-body text-[hsl(348_40%_45%)]">No symptoms yet — your body's notes will live here.</p>
              ) : (
                <ul className="mt-4 space-y-2 max-h-[220px] overflow-y-auto scrollbar-thin pr-1" data-testid="pregnancy-symptom-list">
                  {symptoms.slice(0, 8).map((s) => (
                    <li key={s.id} className="flex items-start gap-3 bg-white rounded-2xl p-3 border border-[hsl(348_40%_92%)]">
                      <div className="text-[11px] uppercase tracking-wider font-semibold opacity-65 shrink-0 w-14 text-[hsl(348_45%_35%)]">
                        {fmtSafe(s.date, "MMM d")}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap gap-1.5">
                          {(s.symptoms || []).map((sx, i) => (
                            <span key={i} className="chip chip-coral !text-[10px] !py-0.5 !px-2">{sx}</span>
                          ))}
                        </div>
                        {s.note && <p className="text-[12.5px] opacity-75 mt-1.5 font-body">{s.note}</p>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="pt-2 pb-1 flex items-center justify-center">
              <p className="font-editorial-italic text-sm text-[hsl(348_40%_45%)]">
                A quiet companion through every week.
              </p>
            </div>

            {/* ── Partner banner ── */}
            <div className="mt-3 mx-0 rounded-[24px] border border-[hsl(348_35%_88%)] bg-gradient-to-br from-[hsl(348_70%_97%)] to-[hsl(348_55%_93%)] p-4 shadow-sm" data-testid="pregnancy-partner-banner">
              <p className="text-[9.5px] uppercase tracking-[0.2em] font-extrabold text-[hsl(348_40%_55%)] mb-2.5">Also explore</p>
              <div className="flex gap-2.5">
                <a
                  href="https://www.fertiora.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex flex-col items-center justify-center gap-1 rounded-2xl bg-white/70 border border-[hsl(348_30%_88%)] py-3 px-2 hover:bg-white transition-colors active:scale-[0.97]"
                  data-testid="banner-fertiora"
                >
                  <span className="text-[18px]">🌸</span>
                  <span className="font-extrabold text-[12px] text-[hsl(348_55%_30%)]">Fertiora</span>
                  <span className="text-[9.5px] text-[hsl(348_35%_50%)] text-center leading-snug">Fertility &amp; hormonal health</span>
                </a>
                <a
                  href="https://garth.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex flex-col items-center justify-center gap-1 rounded-2xl bg-white/70 border border-[hsl(348_30%_88%)] py-3 px-2 hover:bg-white transition-colors active:scale-[0.97]"
                  data-testid="banner-garth"
                >
                  <span className="text-[18px]">🤱</span>
                  <span className="font-extrabold text-[12px] text-[hsl(348_55%_30%)]">Garth.ai</span>
                  <span className="text-[9.5px] text-[hsl(348_35%_50%)] text-center leading-snug">AI companion for new moms</span>
                </a>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ===== LMP onboarding sheet ===== */}
      <Sheet open={setupOpen} onOpenChange={setSetupOpen}>
        <SheetContent side="bottom" className="rounded-t-[32px] max-w-[480px] mx-auto bg-[hsl(348_70%_97%)] border-t-0 pb-8" data-testid="pregnancy-setup-sheet">
          <SheetHeader className="text-left">
            <div className="flex items-center gap-2.5 mb-1">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-gradient-to-br from-[hsl(348_75%_70%)] to-[hsl(348_70%_60%)]">
                <Baby size={16} className="text-white" />
              </span>
              <p className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[hsl(348_50%_45%)]">Begin</p>
            </div>
            <SheetTitle className="font-editorial text-[26px] leading-tight text-[hsl(348_55%_22%)]">When did your last period start?</SheetTitle>
            <SheetDescription className="text-sm text-[hsl(348_40%_45%)] font-body">
              We'll use this to calculate your weeks and estimated due date.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-4">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="secondary" className="w-full h-14 justify-start gap-2 rounded-2xl text-base bg-white" data-testid="pregnancy-lmp-button">
                  <CalendarDays size={18} />
                  {lmpDate ? format(lmpDate, "MMMM d, yyyy") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="p-0 w-auto">
                <Calendar mode="single" selected={lmpDate} onSelect={setLmpDate} disabled={(d) => d > new Date()} initialFocus />
              </PopoverContent>
            </Popover>
          </div>

          <div className="mt-5 flex gap-2">
            <button onClick={() => setSetupOpen(false)} className="btn-pill-cream flex-1" data-testid="pregnancy-setup-cancel">Not now</button>
            <button onClick={submitLmp} disabled={!lmpDate} className="btn-pill-coral flex-1" data-testid="pregnancy-setup-save">
              Save <ArrowUpRight size={14} />
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {/* ===== Symptom log sheet ===== */}
      <Sheet open={symptomOpen} onOpenChange={setSymptomOpen}>
        <SheetContent side="bottom" className="rounded-t-[32px] max-w-[480px] mx-auto bg-[hsl(348_70%_97%)] border-t-0 pb-8" data-testid="pregnancy-symptom-sheet">
          <SheetHeader className="text-left">
            <SheetTitle className="font-editorial text-[26px] leading-tight text-[hsl(348_55%_22%)]">What's your body saying?</SheetTitle>
            <SheetDescription className="text-sm text-[hsl(348_40%_45%)] font-body">
              Tap as many as you feel today. Knya tracks the pattern over time.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-4 flex flex-wrap gap-1.5 max-h-[280px] overflow-y-auto scrollbar-thin pr-1">
            {PREG_SYMPTOMS.map((s) => {
              const active = selectedSymptoms.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSelectedSymptoms(active ? selectedSymptoms.filter((x) => x !== s) : [...selectedSymptoms, s])}
                  className={`px-3 py-1.5 rounded-full text-[12px] font-medium border transition ${
                    active
                      ? "bg-gradient-to-br from-[hsl(348_75%_70%)] to-[hsl(348_70%_60%)] text-white border-transparent shadow-[0_6px_14px_-6px_hsla(348,70%,60%,0.55)]"
                      : "bg-white text-[hsl(348_50%_28%)] border-[hsl(348_40%_88%)] hover:border-[hsl(348_60%_70%)]"
                  }`}
                  data-testid={`pregnancy-symptom-${s.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {s}
                </button>
              );
            })}
          </div>

          <Textarea
            placeholder="Note for Knya (optional)"
            value={symptomNote}
            onChange={(e) => setSymptomNote(e.target.value)}
            className="mt-4 min-h-[60px] bg-white rounded-2xl border-[hsl(348_40%_88%)]"
            data-testid="pregnancy-symptom-note"
          />

          <div className="mt-5 flex gap-2">
            <button onClick={() => setSymptomOpen(false)} className="btn-pill-cream flex-1" data-testid="pregnancy-symptom-cancel">Cancel</button>
            <button onClick={submitSymptoms} className="btn-pill-coral flex-1" data-testid="pregnancy-symptom-save">
              Save <ArrowUpRight size={14} />
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
